import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Trophy,
  Newspaper,
  Image,
  MessageSquare,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronRight
} from 'lucide-react';
import { adminAPI } from '@/lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5aeb9d65-c68c-4f8e-b371-f49441118154/artifacts/y8ms66px_06.png";

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Users, label: 'Atletas', path: '/admin/atletas' },
  { icon: Calendar, label: 'Eventos', path: '/admin/eventos' },
  { icon: Trophy, label: 'Conquistas', path: '/admin/conquistas' },
  { icon: Newspaper, label: 'Notícias', path: '/admin/noticias' },
  { icon: Image, label: 'Galeria', path: '/admin/galeria' },
  { icon: MessageSquare, label: 'Contatos', path: '/admin/contatos' },
  { icon: Settings, label: 'Configurações', path: '/admin/config' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminAPI.stats();
        setStats(res.data);
      } catch (error) {
        console.error('Erro ao carregar stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 dark" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="ACEC" className="h-8 w-auto" />
          <span className="font-heading font-bold">ADMIN</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="toggle-mobile-sidebar"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 admin-sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ACEC" className="h-10 w-auto" />
              <div>
                <span className="font-heading font-bold text-white text-lg">ACEC</span>
                <span className="block text-xs text-slate-400">Painel Admin</span>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 overflow-y-auto">
            <ul className="space-y-1 px-3">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`admin-nav-${item.label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all admin-nav-item ${
                        isActive ? 'active text-white' : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      <item.icon size={20} />
                      <span className="font-medium">{item.label}</span>
                      {item.label === 'Contatos' && stats?.contatos_nao_lidos > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {stats.contatos_nao_lidos}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-slate-700">
            <Link
              to="/"
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-site"
            >
              <LogOut size={18} />
              <span className="text-sm">Voltar ao Site</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet context={{ stats }} />
        </div>
      </main>
    </div>
  );
}
